import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import z from "zod";
import { Login } from "@/components/Organisms/Login/Login";
import { useAuth } from "@/contexts/AuthProvider";
import { getAuthState } from "@/stores/auth-store";
import { logApp } from "@/utils/logApp";

export const Route = createFileRoute("/login")({
	/**
	 * Redirect authenticated users away from login page
	 *
	 * Uses AuthContext instead of directly accessing localStorage,
	 * centralizing all authentication logic in the AuthProvider.
	 */
	validateSearch: (search) => {
		return z
			.object({
				redirect: z.string().optional(),
				organizationId: z.string().optional(),
				reason: z
					.enum(["access-removed", "user-not-active", "organization-not-active"])
					.optional(),
				email: z.string().optional(),
			})
			.parse(search);
	},
	beforeLoad: ({ search }) => {
		const auth = getAuthState();

		if (auth.isAuthenticated) {
			// User is already authenticated, redirect to dashboard or return URL
			throw redirect({
				to: search.redirect || "/web/$orgId/home",
				replace: true,
			});
		}
	},

	component: LoginPage,
});

function LoginPage() {
	const auth = useAuth();
	const { redirect: redirectTo, organizationId, reason, email: emailFromSearch } =
		Route.useSearch();
	const [email, setEmail] = useState(emailFromSearch ?? "");

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		try {
			console.log("🔄 Login attempt for email:", email);

			// ✅ Construct the proper return URL for after login
			let finalReturnUrl = "/web/$orgId/home"; // Default fallback

			// When switching org (organizationId in URL), always send user to new org's home.
			// Otherwise we'd use the stored redirect which points at the old org's path.
			if (!organizationId && redirectTo) {
				// Decode the redirect parameter (it's URL-encoded from route guards)
				const decodedRedirect = decodeURIComponent(redirectTo);

				// Clean up any malformed data
				const cleanRedirect = decodedRedirect
					.replace(/\[object Object\]/g, "")
					.trim();

				if (
					cleanRedirect &&
					cleanRedirect !== "/web/$orgId/home" &&
					cleanRedirect.length > 1
				) {
					// If it's already a full URL, use it directly
					if (cleanRedirect.startsWith("http")) {
						finalReturnUrl = cleanRedirect;
					} else {
						// If it's a path, construct the full URL
						finalReturnUrl = `${window.location.origin}${cleanRedirect}`;
					}
				}
			}

			console.log("🎯 Final return URL for after login:", finalReturnUrl);

			await auth.login(email, finalReturnUrl);
		} catch (error) {
			console.error("❌ Login failed:", error);
			logApp({
				level: "error",
				message: `Login failed: ${error}`,
			});
		}
	}

	// ✅ No useEffect needed - auth store handles redirect after OAuth callback
	// The auth store subscription (lines 423-464 in auth-store.ts) will automatically
	// redirect to the stored URL after successful authentication

	return (
		<Login
			submitFunction={handleSubmit}
			email={email}
			setEmail={setEmail}
			accessRemoved={reason === "access-removed"}
			userNotActive={reason === "user-not-active"}
			organizationNotActive={reason === "organization-not-active"}
		/>
	);
}
